import { useReducer, useContext, useMemo } from 'react';
import { createAction, handleActions } from 'redux-actions';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { useDeepEqualMemo } from '@wix/da-hooks/pkg/useDeepEqualMemo';
import { BiEvent } from '@wix/da-bi/pkg/events';

interface State {
  showTooltip: 'tooltip' | 'popover' | false;
}

const onMouseEnter = createAction('mouseenter', (isMobile: boolean) => ({
  isMobile,
}));
const onMouseLeave = createAction('mouseleave', () => ({}));
const onReferenceClick = createAction('refclick', (hasPopover: boolean) => ({
  hasPopover,
}));
const onClose = createAction('hide', () => ({}));

const reducer = handleActions<State, any>(
  {
    [onMouseEnter.toString()]: (
      state,
      { payload: { isMobile } }: ReturnType<typeof onMouseEnter>
    ) => (state.showTooltip || isMobile ? state : { showTooltip: 'tooltip' }),
    [onMouseLeave.toString()]: state =>
      state.showTooltip === 'tooltip' ? { showTooltip: false } : state,
    [onReferenceClick.toString()]: (
      state,
      { payload: { hasPopover } }: ReturnType<typeof onReferenceClick>
    ) => {
      if (!hasPopover || state.showTooltip === 'popover') {
        return { showTooltip: false };
      }

      return { showTooltip: 'popover' };
    },
    [onClose.toString()]: state =>
      state.showTooltip
        ? {
            ...state,
            showTooltip: false,
          }
        : state,
  },
  { showTooltip: false }
);

export function usePopoverOrTooltip(
  hasPopover: boolean,
  hasTooltip: boolean,
  openBiData?: BiEvent,
  closeBiData?: BiEvent
) {
  const isMobile = useContext(MobileContext);
  const [state, dispatch] = useReducer(reducer, {
    showTooltip: false,
  });
  const logBiEvent = useBiLogger();
  const biData = useDeepEqualMemo(state?.showTooltip === 'popover' ? closeBiData : openBiData);

  return useMemo(
    () => ({
      showTooltip: (state && state.showTooltip) || false,
      onMouseEnter: hasTooltip ? () => dispatch(onMouseEnter(isMobile)) : undefined,
      onMouseLeave: hasTooltip ? () => dispatch(onMouseLeave()) : undefined,
      onReferenceClick: () => {
        biData && logBiEvent(biData);
        dispatch(onReferenceClick(hasPopover));
      },
      onClose: () => dispatch(onClose()),
    }),
    [biData, hasPopover, hasTooltip, isMobile, logBiEvent, state]
  );
}
